import {
  AgreementType,
  AnswersAndQuestionFlow,
  AssessmentQuestion,
  BaseUserProfile,
  DietGoal,
  Guardian,
  LegalAgreement,
  MealPreferences,
  NotFoundError,
  ProgramType,
} from 'fitbeat.models';
import { BigBodyGroup, CardioOrResistance, IOnboardingAnswers, UserProfile, WorkoutSettings } from '../../entities';
import { DbWorkoutRepository } from '../../persistence';
import { UserProfileService } from '../user';
import { IOnboardingManager } from './ionboardingmanager';
import {
  mappedCardioOrResistance,
  mappedFoodPreference,
  mappedGender,
  mappedGoal,
  mappedLevel,
} from './onboardingHelper';

export class OnboardingManager implements IOnboardingManager {
  public static getAllQuestions() {
    /*
     * the question order is determined by:
     * - the answer based on `AnswersAndQuestionFlow`
     * - then the question based on the index.
     * see tests/features/assessment-questionnaire.feature for order
     */
    const questions = [
      new AssessmentQuestion('goal', 'Select fitness goal', [
        new AnswersAndQuestionFlow('Muscle & Strength', 'skill'),
        new AnswersAndQuestionFlow('Lean & Fit', 'skill'),
        new AnswersAndQuestionFlow('Mobility & Tone', 'skill'),
        new AnswersAndQuestionFlow('Muscle & Shred', 'skill'),
        new AnswersAndQuestionFlow('All-Rounder', 'burn-build'),
      ]),
      new AssessmentQuestion('skill', 'Select skill level', [
        new AnswersAndQuestionFlow('Basic', 'gender'),
        new AnswersAndQuestionFlow('Intermediate', 'gender'),
        new AnswersAndQuestionFlow('Advanced', 'gender'),
      ]),
      new AssessmentQuestion('gender', 'Select gender', [
        new AnswersAndQuestionFlow('Female', 'nutrition'),
        new AnswersAndQuestionFlow('Male', 'nutrition'),
        new AnswersAndQuestionFlow('Other', 'nutrition'),
      ]),
      new AssessmentQuestion('nutrition', 'Nutrition preference', [
        new AnswersAndQuestionFlow('Vegan', 'combat'),
        new AnswersAndQuestionFlow('Vegetarian', 'combat'),
        new AnswersAndQuestionFlow('All food groups', 'combat'),
      ]),
      new AssessmentQuestion('burn-build', 'First workout preference', [
        new AnswersAndQuestionFlow('Build', 'skill'),
        new AnswersAndQuestionFlow('Burn', 'skill'),
      ]),
      new AssessmentQuestion('combat', 'Include combat exercises', [
        new AnswersAndQuestionFlow('Yes', 'date-of-birth'),
        new AnswersAndQuestionFlow('No', 'date-of-birth'),
      ]),
      new AssessmentQuestion('date-of-birth', 'Date of birth', []),
      new AssessmentQuestion('guardian', 'Legal guardian to complete', []),
      new AssessmentQuestion('terms-and-conditions', 'Accept Terms and Conditions', []),
      new AssessmentQuestion('medical-check', 'Medical check', []),
    ];

    const indexFirst = 0;
    const indexLast = questions.length - 1;

    questions[indexFirst].isFirstQuestion = true;
    questions[indexFirst].isCurrent = true;
    questions[indexLast].isLastQuestion = true;

    return questions;
  }

  public static getNextQuestion(questionsAndAnswers: AssessmentQuestion[], answer: string):
    AssessmentQuestion[] | undefined {
    const currentAssessmentQuestion = questionsAndAnswers.find((question) => question.isCurrent === true);

    if (currentAssessmentQuestion?.questionId === 'date-of-birth') {
      const isMinor = BaseUserProfile.isMinor(answer);
      if (isMinor) {
        this.forceCurrentQuestion(questionsAndAnswers, 'guardian', answer);
        return questionsAndAnswers;
      }

      this.forceCurrentQuestion(questionsAndAnswers, 'terms-and-conditions', answer);
      return questionsAndAnswers;
    }

    if (currentAssessmentQuestion?.questionId === 'guardian') {
      return this.forceCurrentQuestion(questionsAndAnswers, 'medical-check', answer);
    }

    if (currentAssessmentQuestion) {
      const chosenAnswerFlow = currentAssessmentQuestion.answersAndQuestionFlow.find((x: any) => x.answer === answer);
      if (chosenAnswerFlow) {
        this.answerCurrentQuestionAndGoTo(questionsAndAnswers, answer, chosenAnswerFlow.nextQuestionId);
      } else {
        this.answerCurrentQuestionAndGoToRelative(questionsAndAnswers, currentAssessmentQuestion, answer, +1);
      }
      // TODO: this else case does not answer the current question (fine since last question is handled as complete)
      return questionsAndAnswers;
    }
    return questionsAndAnswers;
  }

  public static getPreviousQuestion(questionsAndAnswers: AssessmentQuestion[]): AssessmentQuestion[] | undefined {
    const currentAssessmentQuestion = questionsAndAnswers.find((question) => question.isCurrent === true);

    if (currentAssessmentQuestion?.questionId === 'terms-and-conditions') {
      return OnboardingManager.forceCurrentQuestion(questionsAndAnswers, 'date-of-birth');
    }

    if (currentAssessmentQuestion?.questionId === 'medical-check') {
      const answer = questionsAndAnswers
        .find((question: AssessmentQuestion) => question.questionId === 'guardian')?.answer;

      if (answer) {
        return OnboardingManager.forceCurrentQuestion(questionsAndAnswers, 'guardian', answer);
      } else {
        return OnboardingManager.forceCurrentQuestion(questionsAndAnswers, 'terms-and-conditions', answer);
      }
    }

    if (currentAssessmentQuestion) {
      const previousQuestion = questionsAndAnswers.find((question: AssessmentQuestion) =>
        question.answersAndQuestionFlow.find((options: AnswersAndQuestionFlow) =>
          (options.answer === question.answer) && (options.nextQuestionId === currentAssessmentQuestion.questionId)));
      if (previousQuestion) {
        this.answerCurrentQuestionAndGoTo(questionsAndAnswers, '', previousQuestion.questionId);
      } else {
        const setAndAdjustedToPrevious = this.answerCurrentQuestionAndGoToRelative(
          questionsAndAnswers, currentAssessmentQuestion, '', -1);
        if (!setAndAdjustedToPrevious) {
          this.answerCurrentQuestionAndGoTo(questionsAndAnswers, '', currentAssessmentQuestion.questionId);
        }
      }
      return questionsAndAnswers;
    }
    return questionsAndAnswers;
  }

  public static answerCurrentQuestionAndGoTo(
    questionAndAnswers: AssessmentQuestion[],
    answer: string,
    nextQuestionId: string,
  ) {
    return this.forceCurrentQuestion(questionAndAnswers, nextQuestionId, answer);
  }

  /**
   * @deprecated this answers and forces the current question, but if the given answer is falsy, it sets it
   * as an empty string, which may be unexpected
   * need to replace with a function that merely forces
   */
  public static forceCurrentQuestion(questionAndAnswers: AssessmentQuestion[], questionId: string, answer?: string) {
    for (const question of questionAndAnswers) {
      if (question.isCurrent) {
        question.answer = answer || '';
      }

      question.isCurrent = question.questionId === questionId;
    }
    return questionAndAnswers;
  }

  private static answerCurrentQuestionAndGoToRelative(
    questionsAndAnswers: AssessmentQuestion[],
    currentAssessmentQuestion: AssessmentQuestion,
    answer: string,
    relative: number,
  ): boolean {
    const index = questionsAndAnswers.indexOf(currentAssessmentQuestion);
    const newIndex = index + relative;
    if (newIndex >= 0 && newIndex < questionsAndAnswers.length) {
      const nextQuestionId = questionsAndAnswers[newIndex].questionId;
      this.answerCurrentQuestionAndGoTo(questionsAndAnswers, answer, nextQuestionId);
      return true;
    }
    return false;
  }

  private static getOnboardingAnswers(questionsAndAnswers: AssessmentQuestion[]) {
    const findQuestion = (questionId: string) => {
      const question = questionsAndAnswers.find((q) => q.questionId === questionId)!;
      if (!question) {
        throw new Error(`No such question found ${questionId}`);
      }

      return question;
    };

    const buildGuardianDetails = () => {
      let guardian: Guardian = new Guardian();
      const answer = findQuestion('guardian').answer;

      guardian = JSON.parse(answer);
      guardian.legalAgreements = [];
      guardian.legalAgreements.push(LegalAgreement.createSignedInstance(AgreementType.Platform));
      guardian.legalAgreements.push(LegalAgreement.createSignedInstance(AgreementType.Privacy));
      guardian.legalAgreements.push(LegalAgreement.createSignedInstance(AgreementType.Membership));
      guardian.legalAgreements.push(LegalAgreement.createSignedInstance(AgreementType.Home));

      return guardian;
    };

    const buildLegalAgreements = (): LegalAgreement[] => {
      const results: LegalAgreement[] = [];
      if (findQuestion('medical-check').answer === 'Yes') {
        results.push(LegalAgreement.createSignedInstance(AgreementType.Medical));
      }
      if (findQuestion('terms-and-conditions').answer === 'StudioHomePlatformPrivacy') {
        results.push(LegalAgreement.createSignedInstance(AgreementType.Membership));
        results.push(LegalAgreement.createSignedInstance(AgreementType.Home));
        results.push(LegalAgreement.createSignedInstance(AgreementType.Platform));
        results.push(LegalAgreement.createSignedInstance(AgreementType.Privacy));
      }
      return results;
    };

    return {
      longTermGoal: mappedGoal.get(findQuestion('goal').answer)!,
      skillLevel: mappedLevel.get(findQuestion('skill').answer)!,
      gender: mappedGender.get(findQuestion('gender').answer)!,
      foodPreference: mappedFoodPreference.get(findQuestion('nutrition').answer)!,
      cardioOrResistance: mappedCardioOrResistance.get(findQuestion('burn-build').answer)!,
      combat: findQuestion('combat').answer === 'Yes',
      dateOfBirth: findQuestion('date-of-birth').answer,
      guardian: findQuestion('guardian').answer ? buildGuardianDetails() : undefined,
      agreements: buildLegalAgreements(),
    } as IOnboardingAnswers;
  }

  constructor(private userProfileService: UserProfileService, private dbWorkoutRepository: DbWorkoutRepository) {}

  public async getWorkoutSettingsFromOnboardingAnswers(workoutId: string, questionsAndAnswers: AssessmentQuestion[]):
    Promise<WorkoutSettings> {
    const userWorkoutSession = await this.dbWorkoutRepository.getWorkoutSessionFromId(workoutId);

    if (!userWorkoutSession) {
      throw new Error(`No such workout session found ${workoutId}`);
    }

    const answers = OnboardingManager.getOnboardingAnswers(questionsAndAnswers);

    const getCardioOrResistance = () => {
      if (answers.longTermGoal === ProgramType.GainMuscleAndStrengthGoal) {
        return CardioOrResistance.Resistance;
      }
      if (answers.longTermGoal === ProgramType.GetLeanAndFitGoal) {
        return CardioOrResistance.Cardio;
      }
      if (answers.longTermGoal === ProgramType.MobilityAndTone) {
        return CardioOrResistance.Balance;
      }
      if (answers.longTermGoal === ProgramType.MuscleAndShred) {
        return CardioOrResistance.Resistance;
      }
      return answers.cardioOrResistance;
    };

    return {
      ...userWorkoutSession.settings,
      allowBagContact: answers.combat,
      cardioOrResistance: getCardioOrResistance(),
      level: answers.skillLevel,
      threeBodyGroups: [BigBodyGroup.BackAndArms, BigBodyGroup.Lower, BigBodyGroup.Upper],
    };
  }

  public async updateUserWithOnboardingAnswers(uid: string, questionsAndAnswers: AssessmentQuestion[]) {
    const user = await this.userProfileService.getUserProfile(uid);
    if (!user) {
      throw new NotFoundError('user', uid);
    }

    const answers = OnboardingManager.getOnboardingAnswers(questionsAndAnswers);

    const getMealPreferences = () => {
      const goalMap = new Map<ProgramType, DietGoal>([
        [ProgramType.GainMuscleAndStrengthGoal, DietGoal.BuildMuscle],
        [ProgramType.GetLeanAndFitGoal, DietGoal.BurnFat],
        [ProgramType.GetStrongAndFitGoal, DietGoal.BurnFatAndBuildMuscle],
        [ProgramType.MobilityAndTone, DietGoal.BurnFatAndBuildMuscle],
        [ProgramType.MuscleAndShred, DietGoal.BurnFatAndBuildMuscle],
      ]);

      let mealPreferences = new MealPreferences();
      const dietGoal = goalMap.get(answers.longTermGoal);

      if (!dietGoal) {
        throw new Error(`No such diet goal found: ${answers.longTermGoal}`);
      }

      mealPreferences = {
        foodPreference: answers.foodPreference,
        dietGoal: goalMap.get(answers.longTermGoal)!,
      };

      return mealPreferences;
    };

    const newUserProfile = new UserProfile(user.uid, {
      ...user,
      longTermGoal: answers.longTermGoal,
      skillLevel: answers.skillLevel,
      mealPreferences: getMealPreferences(),
      gender: answers.gender,
      dateOfBirth: answers.dateOfBirth,
      ...(answers.guardian ? { guardianInfo: answers.guardian } : {}),
      legalAgreements: [...user.legalAgreements ?? [], ...answers.agreements],
    });

    const updatedUser = await this.userProfileService.updateOnlyUserChangeableFields(uid, newUserProfile);

    if (!updatedUser) {
      throw new Error(`Failed to retrieve and update user ${newUserProfile.uid}`);
    }

    return updatedUser;
  }
}
